import { useEffect, useState } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from '@mui/material';
import { DownloadIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuthHeader } from '@/hooks/useAuth';
import { getAccountAppSettings } from '@/lib/api/account-app-setting';
import { downloadCSV } from '@/lib/utils';
import type { AccountAppSettingsView } from '@/lib/types/feature/account-app-setting';
import type { SimpleAccount } from '@/lib/types';
import { AccountAppSettingsTable } from './AccountAppSettingsTable';
import { toCsvRows } from './csv';

interface AccountAppSettingsProps {
  account: SimpleAccount;
}

/**
 * アカウントのアプリ設定値（FKV3-13286）
 *
 * 倉庫出荷・店間移動の効果検証を読み解くのに要る設定（展開店舗判定・必要在庫数の補正・
 * 在庫日数の既定値・予測・トレンド判定）を一覧で見せ、CSVでも落とせるようにする。
 * 読み取り専用で、設定の編集はプロダクト側（FULL KAITEN 本体）で行う。
 *
 * 値のラベル・整形はバックエンドが済ませているので、ここは並べるだけにしている。
 */
export function AccountAppSettings({ account }: AccountAppSettingsProps) {
  const { getAuthHeader } = useAuthHeader();

  const [view, setView] = useState<AccountAppSettingsView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!account.code) return;
    let cancelled = false;
    const authHeaders = getAuthHeader();

    setLoading(true);
    setLoadError(null);
    getAccountAppSettings(account.code, authHeaders)
      .then((result) => {
        if (cancelled) return;
        setView(result);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setView(null);
        setLoadError(error instanceof Error ? error.message : '設定値の取得に失敗しました');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // getAuthHeader は毎レンダー新しい関数になるため依存に入れない（入れると取得が止まらない）
  }, [account.code]);

  const handleDownloadCSV = () => {
    if (!view || view.rows.length === 0) return;
    // アカウントと取得日時はCSVの列に持たずファイル名で表す（全行で同じ値になるため）。
    // 日時は取得日時（JST）から作る。new Date() だとダウンロード時刻になり取得時刻とずれる
    const timestamp = view.generated_at.replace(/\D/g, '');
    downloadCSV(toCsvRows(view.rows), `${timestamp}_${view.account_code}_設定値.csv`);
    toast.success('CSVをダウンロードしました');
  };

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6">アカウント設定値</Typography>
            <Typography variant="body2" color="text.secondary">
              効果検証の数字を読み解くのに要る設定値（参照のみ）。変更は FULL KAITEN
              本体の設定画面で行う
            </Typography>
          </Box>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadCSV}
            disabled={!view || view.rows.length === 0}
          >
            <DownloadIcon className="mr-2 h-4 w-4" />
            CSVダウンロード
          </Button>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && loadError && <MuiAlert severity="error">{loadError}</MuiAlert>}

        {!loading && !loadError && view && (
          <>
            <AccountAppSettingsTable rows={view.rows} />
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
              取得日時: {view.generated_at}（JST）／
              ポテンシャル分析の非稼働判定日数は「ポテンシャル分析設定」タブ
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
}
